<template>
  <div class="tab-container">
    <div class="tab-scroll-container" ref="scrollContainer">
      <div class="tab-list" ref="tabList">
        <div
          v-for="(tab, index) in tabs"
          :key="index"
          class="tab-item"
          :class="{ active: activeIndex === index }"
          @click="handleTabClick(index)"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
          ref="tabItems"
        >
          {{ tab.title }}
        </div>

        <div
          class="tab-indicator"
          :style="indicatorStyle"
          ref="indicator"
        ></div>
      </div>
    </div>

    <div class="tab-content">
      <slot :activeIndex="activeIndex"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "AnimatedTabs",
  props: {
    tabs: {
      type: Array,
      required: true,
      default: () => [],
    },
    initialActiveIndex: {
      type: Number,
      default: 0,
    },
    indicatorHeight: {
      type: String,
      default: "3px",
    },
    indicatorColor: {
      type: String,
      default: "#1457df",
    },
    transitionDuration: {
      type: String,
      default: "0.3s",
    },
    indicatorWidth: {
      type: String,
      default: "auto",
    },
  },
  data() {
    return {
      activeIndex: this.initialActiveIndex,
      indicatorStyle: {
        width: "0",
        left: "0",
        height: this.indicatorHeight,
        backgroundColor: this.indicatorColor,
        transition: `width ${this.transitionDuration}, left ${this.transitionDuration}`,
      },
      touchStartX: 0,
      touchStartTime: 0,
      isDragging: false,
    };
  },
  watch: {
    activeIndex(newIndex) {
      this.updateIndicatorPosition(newIndex);
      this.scrollToActiveTab();
    },
    tabs() {
      this.$nextTick(() => {
        this.updateIndicatorPosition(this.activeIndex);
      });
    },
    indicatorWidth() {
      this.$nextTick(() => {
        this.updateIndicatorPosition(this.activeIndex);
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateIndicatorPosition(this.activeIndex);
      window.addEventListener("resize", this.handleResize);
      this.$refs.tabList.addEventListener("mousemove", this.handleMouseMove);
      this.$refs.tabList.addEventListener("mouseleave", this.handleMouseLeave);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.$refs.tabList.removeEventListener("mousemove", this.handleMouseMove);
    this.$refs.tabList.removeEventListener("mouseleave", this.handleMouseLeave);
  },
  methods: {
    handleTabClick(index) {
      if (this.activeIndex !== index) {
        this.activeIndex = index;
        this.$emit("tab-change", index);
      }
    },

    // 修复auto宽度计算逻辑
    updateIndicatorPosition(index) {
      const tabItems = this.$refs.tabItems;
      // 确保tabItems存在且索引有效
      if (
        !tabItems ||
        !Array.isArray(tabItems) ||
        index < 0 ||
        index >= tabItems.length
      ) {
        return;
      }

      const activeTab = tabItems[index];
      // 确保DOM元素存在
      if (!activeTab || !(activeTab instanceof HTMLElement)) return;

      let indicatorWidth, leftPosition;

      // 处理auto宽度情况 - 修复核心
      if (this.indicatorWidth === "auto") {
        // 强制获取最新宽度
        indicatorWidth = `${activeTab.getBoundingClientRect().width}px`;
        leftPosition = `${activeTab.offsetLeft}px`;
      } else {
        // 固定宽度情况
        indicatorWidth = this.indicatorWidth;
        const tabWidth = activeTab.getBoundingClientRect().width;
        const indicatorWidthNum = parseFloat(this.indicatorWidth);
        leftPosition = `${
          activeTab.offsetLeft + (tabWidth - indicatorWidthNum) / 2
        }px`;
      }

      // 强制更新样式
      this.indicatorStyle = {
        ...this.indicatorStyle,
        width: indicatorWidth,
        left: leftPosition,
      };
    },

    handleMouseMove(e) {
      const tabItems = this.$refs.tabItems;
      if (!tabItems || !tabItems.length) return;

      for (let i = 0; i < tabItems.length; i++) {
        const tab = tabItems[i];
        const rect = tab.getBoundingClientRect();

        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          this.activeIndex !== i
        ) {
          let indicatorWidth, leftPosition;

          if (this.indicatorWidth === "auto") {
            indicatorWidth = `${rect.width}px`;
            leftPosition = `${tab.offsetLeft}px`;
          } else {
            indicatorWidth = this.indicatorWidth;
            const indicatorWidthNum = parseFloat(this.indicatorWidth);
            leftPosition = `${
              tab.offsetLeft + (rect.width - indicatorWidthNum) / 2
            }px`;
          }

          this.indicatorStyle.width = indicatorWidth;
          this.indicatorStyle.left = leftPosition;
          break;
        }
      }
    },

    handleMouseLeave() {
      this.updateIndicatorPosition(this.activeIndex);
    },

    handleResize() {
      this.$nextTick(() => {
        this.updateIndicatorPosition(this.activeIndex);
        this.scrollToActiveTab();
      });
    },

    scrollToActiveTab() {
      const scrollContainer = this.$refs.scrollContainer;
      const tabItems = this.$refs.tabItems;

      if (!scrollContainer || !tabItems || !tabItems[this.activeIndex]) return;

      const activeTab = tabItems[this.activeIndex];
      const containerRect = scrollContainer.getBoundingClientRect();

      const scrollPosition =
        activeTab.offsetLeft +
        activeTab.offsetWidth / 2 -
        containerRect.width / 2;

      scrollContainer.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    },

    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartTime = Date.now();
      this.isDragging = false;
    },

    handleTouchEnd(e) {
      if (this.isDragging) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndTime = Date.now();
      const diffX = Math.abs(touchEndX - this.touchStartX);
      const diffTime = touchEndTime - this.touchStartTime;

      if (diffX < 10 && diffTime < 300) {
        const tabItems = this.$refs.tabItems;
        for (let i = 0; i < tabItems.length; i++) {
          const tab = tabItems[i];
          const rect = tab.getBoundingClientRect();

          if (touchEndX >= rect.left && touchEndX <= rect.right) {
            this.handleTabClick(i);
            break;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-container {
  width: 100%;
  overflow: hidden;
}

.tab-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.tab-scroll-container::-webkit-scrollbar {
  display: none;
}

.tab-scroll-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
  border-bottom: 2px solid #e3e8f1;
}

.tab-list {
  display: flex;
  position: relative;
  white-space: nowrap;
  padding-bottom: 8px;
  justify-content: space-between;

  /* &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(180deg, #e3e8f1 0%, #f5f7fa 100%);
    border-radius: 3px;
  } */
}

.tab-item {
  padding: 12px 20px;
  cursor: pointer;
  position: relative;
  transition: color 0.3s;
  font-size: 16px;
  font-weight: 500;
  &.active {
    color: #1457df;
  }
}

.tab-indicator {
  position: absolute;
  bottom: 0;
  border-radius: 3px;
}

.tab-content {
  padding: 20px 0;
}
</style>
    